/**
 * EventDetailDialog (Story 34.5) — the centered modal that opens when an
 * activity row is clicked. Per the UI directive (§4.1) this is a Radix centered
 * Dialog, NEVER a side Sheet.
 *
 * Shows: the humanized action, actor, full timestamp, a side-by-side before/
 * after diff (changed keys highlighted as added / removed / modified),
 * cfCountry, requestId, cfRay, the (server-masked) ipAddress, and a
 * "Copy event JSON" button.
 *
 * Always-mounted (never `{open && <Dialog/>}`) so AnimatePresence can play the
 * exit animation. The dialog tolerates a null event (renders nothing) for the
 * brief window after close while the exit animation runs.
 */

import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button, cn } from "@provenleap/ui";
import { Copy, Check, X } from "@phosphor-icons/react";
import { buildDiffRows, formatDiffValue, type DiffChangeKind } from "./activityVocab";
import { humanizeEvent } from "./HumanizedAction";
import type { AuditEventDto } from "../../hooks/useAuditEvents";

const KIND_BADGE: Record<DiffChangeKind, string> = {
  added: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  removed: "bg-destructive/10 text-destructive",
  modified: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  unchanged: "bg-muted text-muted-foreground",
};

function MetaField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-[13px] text-foreground break-all">
        {value && value.length > 0 ? value : "—"}
      </span>
      {hint && (
        <span className="text-[10.5px] text-muted-foreground/80 italic">{hint}</span>
      )}
    </div>
  );
}

/** Coarse masks (e.g. `203.0.*.*`, `***`) are surfaced to non-owners. */
function isMaskedIp(ip: string | null | undefined): boolean {
  return !!ip && ip.includes("*");
}

export function EventDetailDialog({
  event,
  open,
  onOpenChange,
}: {
  event: AuditEventDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);

  // Reset the copy affordance whenever the dialog opens for a fresh event.
  useEffect(() => {
    if (open) setCopied(false);
  }, [open, event?.id]);

  const handleCopy = async () => {
    if (!event) return;
    try {
      await navigator.clipboard?.writeText(JSON.stringify(event, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently ignore.
    }
  };

  const diffRows = event
    ? buildDiffRows(event.diffBefore, event.diffAfter).filter((r) => r.kind !== "unchanged")
    : [];
  const hasDiff = diffRows.length > 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && event && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="absolute right-5 top-5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>

                <div className="flex flex-col space-y-1.5 text-left pr-8">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-snug tracking-tight">
                    {event.actorEmailSnapshot ?? "System"}{" "}
                    <span className="font-normal text-muted-foreground">
                      {humanizeEvent(event).replace(/\*\*/g, "")}
                    </span>
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {new Date(event.occurredAt).toLocaleString()}
                  </DialogPrimitive.Description>
                </div>

                {/* Before / after diff */}
                <div className="mt-6">
                  <h3 className="text-[13px] font-semibold text-foreground mb-2">
                    Changes
                  </h3>
                  {hasDiff ? (
                    <div
                      className="rounded-[18px] overflow-hidden"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <div className="grid grid-cols-[1fr_1fr] bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <span className="px-3 py-2">Before</span>
                        <span className="px-3 py-2 border-l-[0.5px] border-input">After</span>
                      </div>
                      {diffRows.map((row) => (
                        <div
                          key={row.key}
                          data-testid="diff-row"
                          data-kind={row.kind}
                          className="grid grid-cols-[1fr_1fr] border-t-[0.5px] border-input text-[12.5px]"
                        >
                          <div className="px-3 py-2 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="font-medium text-foreground break-all">
                                {row.key}
                              </span>
                              <span
                                className={cn(
                                  "rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize",
                                  KIND_BADGE[row.kind]
                                )}
                              >
                                {row.kind}
                              </span>
                            </div>
                            <span className="text-muted-foreground break-all">
                              {formatDiffValue(row.before)}
                            </span>
                          </div>
                          <div className="px-3 py-2 min-w-0 border-l-[0.5px] border-input">
                            <span className="text-foreground break-all">
                              {formatDiffValue(row.after)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[13px] text-muted-foreground">
                      No field-level changes were recorded for this event.
                    </p>
                  )}
                </div>

                {/* Request metadata */}
                <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
                  <MetaField label="Country" value={event.cfCountry} />
                  <MetaField
                    label="IP address"
                    value={event.ipAddress}
                    hint={
                      isMaskedIp(event.ipAddress)
                        ? "Masked — the full IP is visible to workspace owners only"
                        : undefined
                    }
                  />
                  <MetaField label="Request ID" value={event.requestId} />
                  <MetaField label="CF-Ray" value={event.cfRay} />
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={handleCopy}
                    className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    {copied ? (
                      <Check size={16} className="mr-1" />
                    ) : (
                      <Copy size={16} className="mr-1" />
                    )}
                    {copied ? "Copied" : "Copy event JSON"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
